import { WarehouseType } from '@elementary/common-ui/types';
import {
  AssetSourceFormData,
  AthenaAuthMethod,
  BigQueryAuthMethod,
  DatabricksAuthMethod,
  DbtAssetSourceFormData,
  SnowflakeAuthMethod,
  WarehouseAssetSourceFormData,
} from 'shared/types';
import { DEFAULT_SSH_PORT, MAX_PORT, MIN_PORT } from './constants';

type WarehouseTerms = {
  databaseLabel: string;
  databaseNoun: string;
  databasePlaceholder: string;
  schemaNoun: string;
  schemasNoun: string;
  schemaPlaceholder: string;
};

export const getWarehouseTerms = (
  warehouseType: WarehouseType,
): WarehouseTerms => {
  switch (warehouseType) {
    case 'bigquery':
      return {
        databaseLabel: 'Project',
        databaseNoun: 'project',
        databasePlaceholder: 'e.g. my-project',
        schemaNoun: 'dataset',
        schemasNoun: 'datasets',
        schemaPlaceholder: 'e.g. my_dataset',
      };
    case 'redshift':
    case 'postgres':
    case 'clickhouse':
      return {
        databaseLabel: 'Database name',
        databaseNoun: 'database',
        databasePlaceholder: 'e.g. analytics',
        schemaNoun: 'schema',
        schemasNoun: 'schemas',
        schemaPlaceholder: 'e.g. public',
      };
    case 'databricks':
      return {
        databaseLabel: 'Catalog',
        databaseNoun: 'catalog',
        databasePlaceholder: 'e.g. hive_metastore',
        schemaNoun: 'schema',
        schemasNoun: 'schemas',
        schemaPlaceholder: 'e.g. default',
      };
    case 'athena':
      return {
        databaseLabel: 'Database',
        databaseNoun: 'database',
        databasePlaceholder: 'e.g. awsdatacatalog',
        schemaNoun: 'schema',
        schemasNoun: 'schemas',
        schemaPlaceholder: 'e.g. analytics',
      };
    case 'fabric':
      return {
        databaseLabel: 'Database',
        databaseNoun: 'database',
        databasePlaceholder: 'e.g. my_warehouse',
        schemaNoun: 'schema',
        schemasNoun: 'schemas',
        schemaPlaceholder: 'e.g. dbo',
      };
    case 'dremio':
      return {
        databaseLabel: 'Object storage',
        databaseNoun: 'object storage',
        databasePlaceholder: '@username',
        schemaNoun: 'object storage path',
        schemasNoun: 'object storage paths',
        schemaPlaceholder: 'e.g. path/to/storage',
      };
    case 'snowflake':
    default:
      return {
        databaseLabel: 'Database name',
        databaseNoun: 'database',
        databasePlaceholder: 'e.g. ANALYTICS',
        schemaNoun: 'schema',
        schemasNoun: 'schemas',
        schemaPlaceholder: 'e.g. PUBLIC',
      };
  }
};

export const getAssetSourceRules = (warehouseType: WarehouseType) => {
  const isClickhouse = warehouseType === 'clickhouse';
  return {
    hasDatabase: !isClickhouse,
    isDbtDatabaseRequired: !isClickhouse && warehouseType !== 'databricks',
    hasWarehouseSchemas: !isClickhouse,
  };
};

export const readErrorMessage = (value: unknown): string | undefined => {
  if (
    value != null &&
    typeof value === 'object' &&
    'message' in value &&
    typeof value.message === 'string'
  ) {
    return value.message;
  }
  return undefined;
};

export const getDefaultDbtAssetSource = (
  warehouseType: WarehouseType,
): DbtAssetSourceFormData => ({
  type: 'dbt',
  name: '',
  schema: '',
  database: getAssetSourceRules(warehouseType).hasDatabase ? '' : undefined,
});

export const getDefaultWarehouseAssetSource =
  (): WarehouseAssetSourceFormData => ({
    type: 'warehouse',
    name: '',
    dbFilters: [{ database: '', schemas: [] }],
  });

export const switchAssetSourceKind = (
  current: AssetSourceFormData,
  kind: AssetSourceFormData['type'],
  warehouseType: WarehouseType,
): AssetSourceFormData => {
  if (current.type === kind) {
    return current;
  }
  if (kind === 'dbt') {
    return { ...getDefaultDbtAssetSource(warehouseType), name: current.name };
  }
  return { ...getDefaultWarehouseAssetSource(), name: current.name };
};

const findOption = <T extends string>(
  options: T[],
  value: unknown,
): T | undefined => options.find((option) => option === value);

export const getSnowflakeAuthMethod = ({
  snowflakeAuthMethod,
  snowflakePassword,
}: Partial<
  Record<'snowflakeAuthMethod' | 'snowflakePassword', unknown>
>): SnowflakeAuthMethod =>
  findOption(Object.values(SnowflakeAuthMethod), snowflakeAuthMethod) ??
  (snowflakePassword != null
    ? SnowflakeAuthMethod.PASSWORD
    : SnowflakeAuthMethod.KEY_PAIR);

export const getAthenaAuthMethod = ({
  athenaAuthMethod,
  athenaAccessKeyId,
  athenaSecretAccessKey,
  athenaRoleArn,
  athenaExternalId,
}: Partial<
  Record<
    | 'athenaAuthMethod'
    | 'athenaAccessKeyId'
    | 'athenaSecretAccessKey'
    | 'athenaRoleArn'
    | 'athenaExternalId',
    unknown
  >
>): AthenaAuthMethod => {
  const explicit = findOption(
    Object.values(AthenaAuthMethod),
    athenaAuthMethod,
  );
  if (explicit) {
    return explicit;
  }
  const hasSecretAccessKey =
    athenaAccessKeyId != null || athenaSecretAccessKey != null;
  const hasRoleArn = athenaRoleArn != null || athenaExternalId != null;
  return !hasSecretAccessKey && hasRoleArn
    ? AthenaAuthMethod.ROLE_ARN
    : AthenaAuthMethod.SECRET_ACCESS_KEY;
};

export const getBigQueryAuthMethod = ({
  bigqueryAuthMethod,
}: Partial<Record<'bigqueryAuthMethod', unknown>>): BigQueryAuthMethod =>
  findOption(Object.values(BigQueryAuthMethod), bigqueryAuthMethod) ??
  BigQueryAuthMethod.SERVICE_ACCOUNT;

export const getDatabricksAuthMethod = ({
  databricksAuthMethod,
}: Partial<Record<'databricksAuthMethod', unknown>>): DatabricksAuthMethod =>
  findOption(Object.values(DatabricksAuthMethod), databricksAuthMethod) ??
  DatabricksAuthMethod.OAUTH;

export const isValidPort = (port: unknown): port is number =>
  typeof port === 'number' &&
  Number.isInteger(port) &&
  port >= MIN_PORT &&
  port <= MAX_PORT;

export const getSshPortWhenEnabling = (
  isEnabling: boolean,
  currentPort: unknown,
): number | undefined =>
  isEnabling && !isValidPort(currentPort) ? DEFAULT_SSH_PORT : undefined;
